import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  CheckCircle2,
  CreditCard,
  Edit2,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Phone,
  Save,
  User as UserIcon,
  X,
} from "lucide-react";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { EmptyState } from "../../../components/ui/EmptyState";
import { Skeleton } from "../../../components/ui/Skeleton";
import { SectionCard } from "../../../components/ui/SectionCard";
import { FieldLabel } from "../../../components/ui/FieldLabel";
import { EditableField, ReadOnlyField } from "../../../components/ui/EditableField";
import { cn } from "../../../utils/cn";
import { formatRut } from "../../../utils/formatter";
import { formatPhoneCL } from "../../../utils/formatters";
import useAuth from "../../../hooks/useAuth";
import { getUser, updateUserApi } from "../../../api/user";
import { showToast } from "../../../utils/showToast";
import { apiErrorMessage, apiErrorStatus } from "../../../utils/apiErrorMessage";
import AppearancePreferences from "./AppearancePreferences";
import MapStylePreferences from "./MapStylePreferences";
import BankDataSection from "./BankDataSection";

const FIELD_LABELS = {
  name: "Nombre",
  lastName: "Apellido",
  phone: "Teléfono",
  rut: "RUT",
};

function validatePasswordStrength(password) {
  if (!password) return { strength: 0, checks: {}, label: "", tone: "neutral" };
  const checks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    // eslint-disable-next-line no-useless-escape
    special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  };
  const strength = Object.values(checks).filter(Boolean).length;
  let label;
  let tone;
  if (strength <= 2) {
    label = "Débil";
    tone = "error";
  } else if (strength <= 4) {
    label = "Media";
    tone = "warning";
  } else {
    label = "Fuerte";
    tone = "success";
  }
  return { strength, checks, label, tone };
}

export default function MyAccount() {
  const { t } = useTranslation();
  const { user: authUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState({});
  const [tempValues, setTempValues] = useState({
    name: "",
    lastName: "",
    phone: "",
    rut: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!authUser?.id) return;
    let active = true;
    setLoading(true);
    getUser({ id: authUser.id })
      .then((res) => {
        if (!active) return;
        // Preserva el patrón de fallbacks del componente anterior.
        const user = res?.data || res?.data?.user || res?.user;
        if (user && user._id) {
          setUserData(user);
          setTempValues({
            name: user.name || "",
            lastName: user.lastName || "",
            phone: user.phone || "",
            rut: user.rut || "",
            password: "",
            confirmPassword: "",
          });
        } else if (apiErrorStatus(res?.error) !== 401) {
          // A 401 is already reported by the session layer (expired/blocked
          // session toast + logout); toasting its raw body here duplicated it.
          showToast(apiErrorMessage(res?.error, t("Error al cargar los datos del usuario")), false);
        }
      })
      .catch((err) => {
        console.error("Error fetching user data:", err);
        showToast(t("Error al obtener los datos del usuario"), false);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [authUser]);

  const handleEdit = (field) => setEditing((p) => ({ ...p, [field]: true }));

  const handleCancel = (field) => {
    setEditing((p) => ({ ...p, [field]: false }));
    setTempValues((p) => ({
      ...p,
      [field]: field === "password" ? "" : userData?.[field] || "",
      ...(field === "password" ? { confirmPassword: "" } : {}),
    }));
    if (field === "password") {
      setShowPassword(false);
      setShowConfirm(false);
    }
  };

  const handleChange = (field, value) =>
    setTempValues((p) => ({ ...p, [field]: value }));

  const handleSave = async (field) => {
    if (!userData?._id) return;
    const value = tempValues[field];

    if (field === "password") {
      if (!value) {
        showToast(t("La contraseña no puede estar vacía"), false);
        return;
      }
      if (value.length < 8) {
        showToast(t("La contraseña debe tener al menos 8 caracteres"), false);
        return;
      }
      if (!tempValues.confirmPassword) {
        showToast(t("Por favor confirma tu contraseña"), false);
        return;
      }
      if (value !== tempValues.confirmPassword) {
        showToast(t("Las contraseñas no coinciden"), false);
        return;
      }
      const { strength } = validatePasswordStrength(value);
      if (strength < 3) {
        showToast(
          t("La contraseña es muy débil. Incluye mayúsculas, números y caracteres especiales"),
          false,
        );
        return;
      }
    }

    try {
      setSaving(true);
      const updateData = {
        [field]: value || undefined,
        ...(userData.active !== undefined && { active: userData.active }),
      };
      if (field === "password") updateData.password = value;

      const res = await updateUserApi({ userId: userData._id, values: updateData });
      if (res?.error) {
        showToast(res.error?.message || t("Error al actualizar el campo"), false);
        return;
      }

      setUserData((prev) => ({
        ...prev,
        [field]: field === "password" ? prev[field] : value,
      }));
      setEditing((p) => ({ ...p, [field]: false }));

      if (field === "password") {
        setTempValues((p) => ({ ...p, password: "", confirmPassword: "" }));
        setShowPassword(false);
        setShowConfirm(false);
        showToast(t("Contraseña actualizada correctamente"), true);
      } else {
        showToast(t("{{field}} actualizado correctamente", { field: t(FIELD_LABELS[field] ?? field) }), true);
      }
    } catch (err) {
      console.error("Error updating field:", err);
      showToast(t("Error al actualizar el campo"), false);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    // El skeleton DEBE espejar el layout real (sin título/subtítulo — el header
    // global ya muestra "Mi cuenta"): grid de 2 columnas, izquierda una card
    // alta (Datos personales), derecha dos cards apiladas (Apariencia + Estilo
    // de mapa). Antes mostraba un título/subtítulo fantasma + dos bloques
    // iguales de 420px → salto de layout ("carga raro") al aparecer el contenido.
    const FieldRow = () => (
      <div className="space-y-1.5">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-9 w-full rounded-lg" />
      </div>
    );
    const CardSkel = ({ rows }) => (
      <div className="rounded-xl border border-border bg-bg p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-4">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-4 w-40" />
        </div>
        <div className="space-y-3">
          {Array.from({ length: rows }).map((_, i) => <FieldRow key={i} />)}
        </div>
      </div>
    );
    return (
      <div className="w-full min-w-0 p-4 md:p-6 space-y-5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <CardSkel rows={6} />
          <div className="space-y-4">
            <CardSkel rows={3} />
            <CardSkel rows={2} />
          </div>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="p-4 md:p-6">
        <EmptyState
          icon={<UserIcon />}
          title={t("No pudimos cargar tu cuenta")}
          description={t("Reintentá en unos segundos o cerrá y volvé a abrir la sesión.")}
          action={
            <Button variant="outline" onClick={() => window.location.reload()}>
              {t("Reintentar")}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 p-6 space-y-5">
      {/* Sin PageHeader: el header global ya muestra "Mi cuenta" con su ícono.
          Las cards (Datos personales, Temas, Modo daltónico, Estilo de mapa) se
          presentan solas sin un H1 repetido. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <SectionCard
          icon={<UserIcon className="w-4 h-4 text-fg-muted" />}
          title={t("Datos personales")}
        >
          <div className="space-y-3">
            <EditableField
              label={t("Nombre")}
              value={userData.name}
              editing={!!editing.name}
              temp={tempValues.name}
              onTempChange={(v) => handleChange("name", v)}
              onEdit={() => handleEdit("name")}
              onSave={() => handleSave("name")}
              onCancel={() => handleCancel("name")}
              saving={saving}
              placeholder={t("Tu nombre")}
            />
            <EditableField
              label={t("Apellido")}
              value={userData.lastName}
              editing={!!editing.lastName}
              temp={tempValues.lastName}
              onTempChange={(v) => handleChange("lastName", v)}
              onEdit={() => handleEdit("lastName")}
              onSave={() => handleSave("lastName")}
              onCancel={() => handleCancel("lastName")}
              saving={saving}
              placeholder={t("Tu apellido")}
            />
            <ReadOnlyField
              label={t("Correo electrónico")}
              icon={<Mail className="w-3.5 h-3.5" />}
              value={userData.email}
              hint={t("El correo no se puede modificar")}
            />
            <EditableField
              label={t("Teléfono")}
              icon={<Phone className="w-3.5 h-3.5" />}
              value={userData.phone}
              displayValue={formatPhoneCL(userData.phone)}
              editing={!!editing.phone}
              temp={tempValues.phone}
              onTempChange={(v) => handleChange("phone", v)}
              onEdit={() => handleEdit("phone")}
              onSave={() => handleSave("phone")}
              onCancel={() => handleCancel("phone")}
              saving={saving}
              placeholder="+56 9 1234 5678"
              type="tel"
            />
            <EditableField
              label={t("RUT")}
              icon={<CreditCard className="w-3.5 h-3.5" />}
              value={userData.rut}
              displayValue={formatRut(userData.rut)}
              editing={!!editing.rut}
              temp={tempValues.rut}
              onTempChange={(v) => handleChange("rut", v)}
              onEdit={() => handleEdit("rut")}
              onSave={() => handleSave("rut")}
              onCancel={() => handleCancel("rut")}
              saving={saving}
              placeholder="12345678-5"
              mono
            />
          </div>

          <div className="pt-4 mt-2 border-t border-border">
            <PasswordSection
              editing={!!editing.password}
              tempPassword={tempValues.password}
              tempConfirm={tempValues.confirmPassword}
              showPassword={showPassword}
              showConfirm={showConfirm}
              saving={saving}
              onEdit={() => handleEdit("password")}
              onCancel={() => handleCancel("password")}
              onSave={() => handleSave("password")}
              onChange={(v) => handleChange("password", v)}
              onChangeConfirm={(v) => handleChange("confirmPassword", v)}
              onTogglePassword={() => setShowPassword((v) => !v)}
              onToggleConfirm={() => setShowConfirm((v) => !v)}
            />
          </div>
        </SectionCard>

        <div className="space-y-4">
          {/* Solo para usuarios marcados como vendedor por su operador: la
              cuenta a la que se le transfiere (liquidaciones COD, comisiones).
              El flag viene del documento del usuario (GET users/:id), no del JWT. */}
          {userData.isSeller && (
            <BankDataSection
              bankData={userData.bankData}
              onSaved={(bd) => setUserData((p) => ({ ...p, bankData: bd }))}
            />
          )}
          <AppearancePreferences />
          <MapStylePreferences />
        </div>
      </div>
    </div>
  );
}

function PasswordSection({
  editing,
  tempPassword,
  tempConfirm,
  showPassword,
  showConfirm,
  saving,
  onEdit,
  onCancel,
  onSave,
  onChange,
  onChangeConfirm,
  onTogglePassword,
  onToggleConfirm,
}) {
  const { t } = useTranslation();
  const strength = validatePasswordStrength(tempPassword);
  const passwordsMatch =
    tempConfirm && tempPassword && tempPassword === tempConfirm;
  const passwordsMismatch =
    tempConfirm && tempPassword && tempPassword !== tempConfirm;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <FieldLabel icon={<Lock className="w-3.5 h-3.5" />}>{t("Contraseña")}</FieldLabel>
        {!editing ? (
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center gap-1.5 h-11 sm:h-8 px-3 rounded-md border border-border bg-bg text-xs font-medium text-fg hover:bg-bg-muted transition-colors"
          >
            <Edit2 className="w-3.5 h-3.5" />
            {t("Cambiar")}
          </button>
        ) : (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={onSave}
              disabled={saving || !tempPassword || !tempConfirm}
              className="inline-flex items-center gap-1.5 h-11 sm:h-8 px-3 rounded-md bg-brand-500 text-white text-xs font-medium hover:bg-brand-600 disabled:opacity-50 transition-colors"
            >
              <Save className="w-3.5 h-3.5" />
              {t("Guardar")}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="inline-flex items-center gap-1.5 h-11 sm:h-8 px-3 rounded-md border border-border bg-bg text-xs font-medium text-fg hover:bg-bg-muted disabled:opacity-50 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              {t("Cancelar")}
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        <input
          type="password"
          value="••••••••••"
          readOnly
          disabled
          className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm text-fg-muted cursor-not-allowed"
        />
      ) : (
        <>
          <PasswordInput
            placeholder={t("Nueva contraseña")}
            value={tempPassword}
            onChange={onChange}
            show={showPassword}
            onToggle={onTogglePassword}
            error={tempPassword && strength.strength < 3}
          />

          {tempPassword && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full bg-bg-muted overflow-hidden">
                  <div
                    className={cn(
                      "h-full transition-all",
                      strength.tone === "error" && "bg-status-error",
                      strength.tone === "warning" && "bg-status-warning",
                      strength.tone === "success" && "bg-status-success",
                    )}
                    style={{ width: `${(strength.strength / 5) * 100}%` }}
                  />
                </div>
                <Badge tone={strength.tone}>{t(strength.label)}</Badge>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <Requirement
                  ok={strength.checks.length}
                  text={t("Al menos 8 caracteres")}
                />
                <Requirement
                  ok={strength.checks.lowercase}
                  text={t("Una letra minúscula")}
                />
                <Requirement
                  ok={strength.checks.uppercase}
                  text={t("Una letra mayúscula")}
                />
                <Requirement ok={strength.checks.number} text={t("Un número")} />
                <Requirement
                  ok={strength.checks.special}
                  text={t("Un carácter especial")}
                />
              </ul>
            </div>
          )}

          <PasswordInput
            placeholder={t("Confirmar contraseña")}
            value={tempConfirm}
            onChange={onChangeConfirm}
            show={showConfirm}
            onToggle={onToggleConfirm}
            error={passwordsMismatch}
            success={passwordsMatch}
          />

          {passwordsMismatch && (
            <p className="text-xs text-status-error flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" />
              {t("Las contraseñas no coinciden.")}
            </p>
          )}
          {passwordsMatch && (
            <p className="text-xs text-status-success flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" />
              {t("Las contraseñas coinciden.")}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function PasswordInput({ placeholder, value, onChange, show, onToggle, error, success }) {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="new-password"
        className={cn(
          "h-10 w-full rounded-lg border bg-bg pl-3 pr-10 text-sm text-fg",
          "focus:outline-none focus:ring-2 focus:ring-brand-500",
          error
            ? "border-status-error"
            : success
            ? "border-status-success"
            : "border-border",
        )}
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-fg-muted hover:text-fg transition-colors"
        aria-label={show ? t("Ocultar contraseña") : t("Mostrar contraseña")}
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

function Requirement({ ok, text }) {
  return (
    <li
      className={cn(
        "flex items-center gap-1.5",
        ok ? "text-status-success" : "text-fg-muted",
      )}
    >
      {ok ? (
        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
      ) : (
        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
      )}
      <span>{text}</span>
    </li>
  );
}

